import type { CameraView } from '@/modules/game-domain/render-snapshot';
import { worldToScreen, screenToWorld } from '@/modules/game-renderer/coordinates';

export interface ApproachPoint { x: number; y: number }
export interface NpcApproachPlan {
  start: ApproachPoint; target: ApproachPoint; dx: number; dy: number;
  startedAtMs: number; durationMs: number; path?: ApproachPoint[];
}
/** Place the entire135px sprite beyond the current transformed viewport, along the road ray. */
export function createNpcApproach(hero: ApproachPoint, roadDirection: ApproachPoint,
  camera: CameraView, viewport: { width: number; height: number }, nowMs: number, speed = 1.6): NpcApproachPlan {
  const length = Math.hypot(roadDirection.x, roadDirection.y);
  const direction = length > 1e-9 ? { x: roadDirection.x / length, y: roadDirection.y / length } : { x: 1, y: 0 };
  const target = { x: hero.x + direction.x * .9, y: hero.y + direction.y * .9 };
  const origin = worldToScreen(target.x, target.y);
  const ray = worldToScreen(direction.x, direction.y);
  const zoom = Math.max(.01, camera.zoom ?? 1), margin = 135;
  const left = camera.finalX - viewport.width / (2 * zoom) - margin;
  const right = camera.finalX + viewport.width / (2 * zoom) + margin;
  const top = camera.finalY - viewport.height / (2 * zoom) - margin;
  const bottom = camera.finalY + viewport.height / (2 * zoom) + margin;
  const intersections = [Math.abs(ray.x) > 1e-9 ? ((ray.x > 0 ? right : left) - origin.x) / ray.x : Infinity,
    Math.abs(ray.y) > 1e-9 ? ((ray.y > 0 ? bottom : top) - origin.y) / ray.y : Infinity].filter(t => t >= 0 && Number.isFinite(t));
  const distance = Math.max(speed * 2, intersections.length ? Math.min(...intersections) : 0) + .01;
  const start = screenToWorld(origin.x + ray.x * distance, origin.y + ray.y * distance);
  return { start, target, dx: -direction.x, dy: -direction.y, startedAtMs: nowMs,
    durationMs: Math.hypot(start.x - target.x, start.y - target.y) / Math.max(.1, speed) * 1000 };
}
export function sampleNpcApproach(plan: NpcApproachPlan, nowMs: number): ApproachPoint & { dx: number; dy: number; moving: boolean; elapsedMs: number } {
  const elapsedMs = Math.max(0, nowMs - plan.startedAtMs), fraction = Math.min(1, elapsedMs / plan.durationMs);
  if (fraction >= 1) return {...plan.target,dx:plan.dx,dy:plan.dy,moving:false,elapsedMs:elapsedMs-plan.durationMs};
  const points = plan.path ?? [plan.start, plan.target];
  const lengths = points.slice(1).map((to,index) => Math.hypot(to.x-points[index]!.x,to.y-points[index]!.y));
  let distance = lengths.reduce((sum,n)=>sum+n,0) * fraction;
  let position = plan.target;
  for (let i=0;i<lengths.length;i++) {
    const length=lengths[i]!,from=points[i]!,to=points[i+1]!;
    if (length>0 && distance<length) {
      const t=distance/length; position={x:from.x+(to.x-from.x)*t,y:from.y+(to.y-from.y)*t}; break;
    }
    distance-=length;
  }
  let dx=plan.dx,dy=plan.dy;
  if (fraction<1) {
    let travelled=lengths.reduce((sum,n)=>sum+n,0)*fraction;
    for(let i=0;i<lengths.length;i++) {
      if(travelled<lengths[i]!) {const from=points[i]!,to=points[i+1]!,n=lengths[i]!;dx=(to.x-from.x)/n;dy=(to.y-from.y)/n;break;}
      travelled-=lengths[i]!;
    }
  }
  return { ...position, dx, dy, moving:fraction<1,elapsedMs:fraction<1?elapsedMs:elapsedMs-plan.durationMs };
}
/** Closest saved road segment supplies only the entrance ray; it never changes gameplay navigation. */
export function nearestRoadDirection(roads: readonly (readonly ApproachPoint[])[], hero: ApproachPoint): ApproachPoint {
  let best = Infinity, direction = { x: 1, y: 0 };
  for (const road of roads) for (let i = 1; i < road.length; i++) {
    const a = road[i - 1]!, b = road[i]!, dx = b.x - a.x, dy = b.y - a.y;
    const length2 = dx * dx + dy * dy;
    if (length2 < 1e-9) continue;
    const t = Math.max(0, Math.min(1, ((hero.x-a.x)*dx + (hero.y-a.y)*dy) / length2));
    const distance = Math.hypot(hero.x-a.x-t*dx, hero.y-a.y-t*dy);
    if (distance < best) { best = distance; direction = { x: dx, y: dy }; }
  }
  return direction;
}
function followSavedRoad(plan: NpcApproachPlan, roads: readonly (readonly ApproachPoint[])[], camera: CameraView,
  viewport: {width:number;height:number}, speed=1.6): NpcApproachPlan {
  let best=Infinity,chosen:readonly ApproachPoint[]|null=null,index=0,projection=plan.target;
  for(const road of roads) for(let i=1;i<road.length;i++) {
    const a=road[i-1]!,b=road[i]!,dx=b.x-a.x,dy=b.y-a.y,n=dx*dx+dy*dy;
    if(n<1e-9)continue;
    const t=Math.max(0,Math.min(1,((plan.target.x-a.x)*dx+(plan.target.y-a.y)*dy)/n));
    const point={x:a.x+t*dx,y:a.y+t*dy},distance=Math.hypot(point.x-plan.target.x,point.y-plan.target.y);
    if(distance<best){best=distance;chosen=road;index=i;projection=point;}
  }
  if(!chosen||best>3)return plan; // Explicit tangent fallback for missing/disconnected road geometry.
  const zoom=Math.max(.01,camera.zoom??1),halfW=viewport.width/(2*zoom)+135,halfH=viewport.height/(2*zoom)+135;
  const outside=(point:ApproachPoint)=>{const p=worldToScreen(point.x,point.y);return Math.abs(p.x-camera.finalX)>halfW||Math.abs(p.y-camera.finalY)>halfH;};
  const outgoing:ApproachPoint[]=[projection];
  for(let i=index;i<chosen.length;i++){
    const point=chosen[i]!,previous=outgoing[outgoing.length-1]!;
    if(Math.hypot(point.x-previous.x,point.y-previous.y)>1e-9)outgoing.push({...point});
    if(outside(point))break;
  }
  if(!outside(outgoing[outgoing.length-1]!)) {
    const last=outgoing[outgoing.length-1]!;
    let tangent={x:0,y:0};
    for(let i=outgoing.length-2;i>=0;i--) {
      const previous=outgoing[i]!,dx=last.x-previous.x,dy=last.y-previous.y;
      if(Math.hypot(dx,dy)>1e-9){tangent={x:dx,y:dy};break;}
    }
    // Projection at a terminal (including repeated terminal vertices) has no outgoing segment.
    // Recover the final nonzero authored tangent instead of invoking the generic +X fallback.
    if(Math.hypot(tangent.x,tangent.y)<1e-9) for(let i=chosen.length-1;i>0;i--) {
      const from=chosen[i-1]!,to=chosen[i]!,dx=to.x-from.x,dy=to.y-from.y;
      if(Math.hypot(dx,dy)>1e-9){tangent={x:dx,y:dy};break;}
    }
    const extension=createNpcApproach(last,tangent,camera,viewport,plan.startedAtMs,speed);
    outgoing.push(extension.start); // Saved road ends inside view: extend its terminal tangent only.
  }
  const path=outgoing.reverse();
  if(Math.hypot(path[path.length-1]!.x-plan.target.x,path[path.length-1]!.y-plan.target.y)>1e-9)path.push(plan.target);
  // If the arrival anchor lies just beyond the road terminal on its continuation,
  // stop there on the way in; do not pass it, reach the terminal, then turn back.
  if(path.length>=3) {
    const a=path[path.length-3]!,b=path[path.length-2]!,c=path[path.length-1]!;
    const dx=b.x-a.x,dy=b.y-a.y,n=dx*dx+dy*dy;
    const t=n>1e-18?((c.x-a.x)*dx+(c.y-a.y)*dy)/n:-1;
    if(t>=0&&t<=1&&Math.hypot(c.x-a.x-t*dx,c.y-a.y-t*dy)<1e-8)path.splice(path.length-2,1);
  }
  const length=path.slice(1).reduce((sum,to,i)=>sum+Math.hypot(to.x-path[i]!.x,to.y-path[i]!.y),0);
  const last=path[path.length-1]!,previous=path[path.length-2]??plan.start;
  const n=Math.hypot(last.x-previous.x,last.y-previous.y)||1;
  return {...plan,start:path[0]!,path,durationMs:length/speed*1000,dx:(last.x-previous.x)/n,dy:(last.y-previous.y)/n};
}
/** One entrance per active event; repeated approach/shop snapshots cannot restart or rotate it. */
export class WorldEventNpcEntrance {
  private key: string | null = null;
  private plan: NpcApproachPlan | null = null;
  show(key: string, approaching: boolean, hero: ApproachPoint, roadDirection: ApproachPoint,
    camera: CameraView, viewport: { width: number; height: number }, nowMs: number, roads: readonly (readonly ApproachPoint[])[] = []): void {
    if (this.key === key) return;
    this.key = key;
    this.plan = followSavedRoad(createNpcApproach(hero, roadDirection, camera, viewport, nowMs), roads, camera, viewport);
    if (!approaching) this.plan.startedAtMs -= this.plan.durationMs;
  }
  sample(nowMs: number): ReturnType<typeof sampleNpcApproach> | null { return this.plan ? sampleNpcApproach(this.plan, nowMs) : null; }
  clear(): void { this.key = null; this.plan = null; }
}
